import React, { useEffect, useState, useMemo } from "react";
import Select from "react-select";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import apiServices from "../../services/service";
import { leadInputData } from "../../shared/constants";

const selectStyles = {
  container: (base) => ({ ...base, fontSize: ".85rem" }),
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? "#456ABB" : base.borderColor,
    boxShadow: state.isFocused ? "0 0 0 1px #456ABB" : "none",
    "&:hover": { borderColor: "#456ABB" },
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#C2DBFF" : state.isFocused ? "#f0f5ff" : "white",
    color: "#222",
  }),
};

const defaultColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  flex: 1,
  minWidth: 120,
};

const colDefs = [
  {
    field: "fullName",
    headerName: "Full Name",
    minWidth: 160,
    flex: 1.2,
    valueFormatter: (p) => p.value || "-",
  },
  {
    field: "companyName",
    headerName: "Company",
    minWidth: 150,
    flex: 1,
    valueFormatter: (p) => p.value || "-",
  },
  {
    field: "email",
    headerName: "Email",
    minWidth: 180,
    flex: 1.3,
    valueFormatter: (p) => p.value || "-",
  },
  {
    field: "phone",
    headerName: "Phone",
    minWidth: 130,
    flex: 0.9,
    valueFormatter: (p) => p.value || "-",
  },
  {
    field: "owner",
    headerName: "Lead Owner",
    minWidth: 140,
    flex: 1,
    valueFormatter: (p) => p.value || "-",
  },
  {
    field: "leadSource",
    headerName: "Lead Source",
    minWidth: 130,
    flex: 0.9,
    valueFormatter: (p) => p.value || "-",
  },
];

const LeadsTeamTab = ({ recordVisibility }) => {
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);

  useEffect(() => {
    apiServices
      .getTeams()
      .then((response) => {
        const raw = response.data;
        const data = Array.isArray(raw) ? raw : raw?.teamList || raw?.teams || raw?.data || [];
        setTeams(data);
      })
      .catch((error) => console.error("Error fetching teams:", error))
      .finally(() => setTeamsLoading(false));
  }, []);

  const teamOptions = useMemo(
    () => teams.map((t) => ({ value: t.id, label: t.teamName || t.name || t.id })),
    [teams]
  );

  const handleTeamChange = (option) => {
    setSelectedTeam(option);
    if (!option) { setLeads([]); return; }
    setLeadsLoading(true);
    apiServices
      .getLeadList({
        ...leadInputData,
        teamId: option.value,
        teamRecordVisibility: recordVisibility?.teamRecordVisibility ?? 'All',
        bucketId: null,
      })
      .then((response) => setLeads(response.data?.leads || []))
      .catch((error) => console.error("Error fetching team leads:", error))
      .finally(() => setLeadsLoading(false));
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 160px)",
        padding: "16px",
        gap: "12px",
        boxSizing: "border-box",
      }}
    >
      <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: "12px" }}>
        <div style={{ width: "280px" }}>
          <Select
            placeholder="Select Team"
            value={selectedTeam}
            onChange={handleTeamChange}
            options={teamOptions}
            isLoading={teamsLoading}
            isClearable
            menuPortalTarget={document.body}
            styles={selectStyles}
          />
        </div>
        {selectedTeam && (
          <span style={{ fontSize: "0.8rem", color: "#666" }}>
            {leadsLoading
              ? "Loading leads..."
              : `${leads.length} lead${leads.length !== 1 ? "s" : ""}`}
          </span>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        <div className="ag-theme-balham" style={{ height: "100%", width: "100%" }}>
          <AgGridReact
            rowData={leads}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            rowHeight={42}
            animateRows
            suppressRowClickSelection
            alwaysShowVerticalScroll
            loading={leadsLoading}
            onGridReady={(params) => params.api.sizeColumnsToFit()}
            overlayNoRowsTemplate={
              selectedTeam
                ? "<span style='color:#999'>No leads found for this team.</span>"
                : "<span style='color:#999'>Select a team to view its leads.</span>"
            }
          />
        </div>
      </div>
    </div>
  );
};

export default LeadsTeamTab;
